import { useLocation } from '@remix-run/react';
import type { Affiliate } from 'types/capi/pdp';
import type { Price } from 'types/capi/shared';
import type { TrackableProduct } from 'types/TrackableProduct';

import type { Variant } from '~/components/Button';
import { ExternalLink } from '~/components/Button';
import AffiliateBuyNowEvent from '~/events/AffiliateBuyNowEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import wct from '~/services/wct.client';
import type { UrlTemplates } from '~/utils/tracking';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

type Props = {
  affiliate: Affiliate;
  buttonPosition: number;
  children: JSX.Element | JSX.Element[] | string;
  price: Price;
  product: TrackableProduct;
  selectedSize?: string;
  urlTemplates: UrlTemplates;
  variant: Variant;
};

const AffiliateLink = ({
  children,
  buttonPosition,
  affiliate,
  variant,
  product,
  selectedSize,
  price,
  urlTemplates
}: Props) => {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot } = useRootData();
  const { pathname, search } = useLocation();

  const isSoleMarket = affiliate.id === 'affiliate:marketplace';

  const onClick = async (url: string, clickRef: string) => {
    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      product,
      url
    });

    if (!isSoleMarket)
      await new AffiliateBuyNowEvent({
        affiliateName: affiliate.name,
        buttonPosition,
        clientIp,
        isBot,
        isReseller: affiliate.is_reseller,
        pagePath,
        price: price.sale ?? price.regular,
        product,
        queryID: localStorage.getItem('queryID'),
        user
      }).send();
  };

  let { url, clickRef } = getTrackedUrlAndClickRef(urlTemplates, {
    buttonPosition,
    user
  });

  if (isSoleMarket && selectedSize && selectedSize !== '6')
    url += `${url.endsWith('/') ? '' : '/'}sizes/${selectedSize}/`;

  return (
    <ExternalLink
      variant={variant}
      target={isSoleMarket ? '_self' : '_blank'}
      rel={isSoleMarket ? '' : 'sponsored nofollow noopener'}
      href={url}
      onClick={() => onClick(url, clickRef)}
    >
      {children}
    </ExternalLink>
  );
};

export default AffiliateLink;
